/**
 * ERPAdvancedProcGrid — Procurement transaction grid with:
 *  - Context menu per row: Edit / View / Delete / Print
 *  - Expandable row: + click shows inline items sub-table
 *  - Same erp-* base CSS as Supplier Master (ERPAdvancedGrid style)
 *
 * Usage:
 *   <ERPAdvancedProcGrid
 *     title="Purchase Invoice"
 *     screenId="4.10.3"
 *     columns={[{field:'invNo',label:'PI NO'}, ...]}
 *     records={[{invNo:'A/PIN/10016', items:[{barcode:'...',itemName:'...',qty:1}]}]}
 *     itemCols={[{field:'barcode',label:'BARCODE'},{field:'itemName',label:'ITEM NAME'},{field:'qty',label:'QTY'}]}
 *     onAddNew={()=>setView('form')}
 *     onEdit={r=>openForm(r)}
 *     onView={r=>...}
 *     onDelete={r=>...}
 *     onPrint={r=>...}
 *   />
 */
import React, { useState, useRef, useEffect } from 'react';
import { Plus, Download, Columns2, Search, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, Edit2, Eye, Trash2, Printer, ArrowLeft } from 'lucide-react';

import './erp-advanced-proc-grid.css';

// ─── Row context menu ──────────────────────────────────────────────────────
function RowMenu({ row, onEdit, onView, onDelete, onPrint }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  return (
    <div ref={ref} className="erp-proc-row-menu">
      <button
        className="erp-proc-ctx-btn"
        data-testid="proc-ctx-btn"
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        title="Actions"
      >
        +
      </button>
      {open && (
        <div className="erp-proc-ctx-menu">
          {onEdit   && <button onClick={() => { onEdit(row);   setOpen(false); }}><Edit2  size={11}/> Edit</button>}
          {onView   && <button onClick={() => { onView(row);   setOpen(false); }}><Eye    size={11}/> View</button>}
          {onDelete && <button className="danger" onClick={() => { onDelete(row); setOpen(false); }}><Trash2  size={11}/> Delete</button>}
          {onPrint  && <button onClick={() => { onPrint(row);  setOpen(false); }}><Printer size={11}/> Print</button>}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────
export default function ERPAdvancedProcGrid({
  title        = '',
  screenId     = '',
  columns      = [],
  records      = [],
  itemCols     = [],
  onBack,           // () => void — navigate back from list
  onAddNew,
  onEdit,
  onView,
  onDelete,
  onPrint,
  totalRecords = 0,
}) {
  const [expanded,  setExpanded]  = useState(new Set());
  const [filters,   setFilters]   = useState({});
  const [pageSize,  setPageSize]  = useState(20);
  const [page,      setPage]      = useState(1);

  const toggleExpand = (idx) => {
    setExpanded(prev => {
      const n = new Set(prev);
      n.has(idx) ? n.delete(idx) : n.add(idx);
      return n;
    });
  };

  // client-side filter
  const filtered = records.filter(r =>
    Object.entries(filters).every(([f, v]) =>
      !v || String(r[f] ?? '').toLowerCase().includes(v.toLowerCase())
    )
  );
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const fromRec = filtered.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const toRec   = Math.min(page * pageSize, filtered.length);

  const defaultItemCols = itemCols.length > 0 ? itemCols : [
    { field: 'barcode',  label: 'BARCODE' },
    { field: 'itemName', label: 'ITEM NAME' },
    { field: 'qty',      label: 'QTY' },
    { field: 'rate',     label: 'RATE' },
    { field: 'amount',   label: 'AMOUNT' },
  ];

  return (
    <div className="erp-module erp-proc-grid-root" data-testid={`proc-grid-${screenId}`}>
      {/* Header */}
      <div className="erp-screen-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {onBack && (
            <button className="erp-btn erp-btn-ghost" data-testid="proc-back-btn"
              onClick={onBack} style={{ marginRight: 4, padding: '2px 8px' }}>
              <ArrowLeft size={13} aria-hidden="true"/> Back
            </button>
          )}
          <span className="erp-screen-title">{title}</span>
        </div>
        <div className="erp-screen-actions">
          <div className="erp-col-chooser-wrapper">
            <button className="erp-btn erp-btn-ghost" data-testid="proc-columns-btn">
              <Columns2 size={14} aria-hidden="true"/> Columns
            </button>
          </div>
          {onAddNew && (
            <button className="erp-btn erp-btn-primary" onClick={onAddNew} data-testid="proc-add-new-btn">
              <Plus size={14} aria-hidden="true"/> Add New
            </button>
          )}
          <div style={{ position: 'relative', display: 'inline-flex' }}>
            <button className="erp-btn erp-btn-ghost" data-testid="proc-export-btn">
              <Download size={14} aria-hidden="true"/> Export
            </button>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="erp-grid-wrapper" style={{ flex: 1, margin: 0, borderRadius: 0 }}>
        <div className="erp-grid-scroll">
          <table className="erp-grid erp-proc-table">
            <thead>
              <tr>
                <th className="erp-proc-expand-col" />
                <th style={{ width: 80, position: 'sticky', left: 0, zIndex: 3 }}>Actions</th>
                {columns.map(c => (
                  <th key={c.field} className="sortable" style={c.width ? { width: c.width } : {}}>
                    {c.label}
                  </th>
                ))}
              </tr>
              <tr className="erp-grid-filter-row">
                <th className="erp-proc-expand-col" />
                <th style={{ position: 'sticky', left: 0, zIndex: 3 }} />
                {columns.map(c => (
                  <th key={c.field}>
                    <div style={{ position: 'relative' }}>
                      <Search size={10} aria-hidden="true" style={{ position:'absolute',left:6,top:6,color:'var(--erp-text-muted)' }}/>
                      <input
                        className="erp-input erp-input-filter"
                        placeholder={`Search in ${c.label}`}
                        data-testid={`proc-filter-${c.field}`}
                        value={filters[c.field] || ''}
                        onChange={e => setFilters(p => ({ ...p, [c.field]: e.target.value }))}
                      />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 2} style={{ padding: 48, textAlign: 'center', color: 'var(--erp-text-muted)' }}>
                    No records found
                  </td>
                </tr>
              ) : (
                paginated.map((row, ri) => {
                  const idx = (page - 1) * pageSize + ri;
                  const isExp = expanded.has(idx);
                  const items = row.items || [];
                  return (
                    <React.Fragment key={idx}>
                      <tr className={isExp ? 'erp-proc-row-expanded' : ''}>
                        <td className="erp-proc-expand-col">
                          <button
                            className={`erp-proc-expand-btn${isExp ? ' open' : ''}`}
                            onClick={() => toggleExpand(idx)}
                            data-testid={`proc-expand-${idx}`}
                            title={isExp ? 'Collapse items' : 'Expand items'}
                          >
                            {isExp ? '▾' : '▸'}
                          </button>
                        </td>
                        <td style={{ position:'sticky', left:0, zIndex:2, background:'var(--erp-bg)' }}>
                          <RowMenu row={row} onEdit={onEdit} onView={onView} onDelete={onDelete} onPrint={onPrint}/>
                        </td>
                        {columns.map(c => (
                          <td key={c.field}>{row[c.field] ?? ''}</td>
                        ))}
                      </tr>
                      {isExp && (
                        <tr className="erp-proc-sub-row">
                          <td colSpan={columns.length + 2} style={{ padding: 0 }}>
                            <div className="erp-proc-sub-wrap">
                              {items.length === 0 ? (
                                <div className="erp-proc-sub-empty">No items recorded for this transaction.</div>
                              ) : (
                                <table className="erp-proc-sub-table">
                                  <thead>
                                    <tr>
                                      <th style={{ width: 40 }}>S.No</th>
                                      {defaultItemCols.map(c => <th key={c.field}>{c.label}</th>)}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {items.map((item, ii) => (
                                      <tr key={ii}>
                                        <td style={{ textAlign:'center' }}>{ii + 1}</td>
                                        {defaultItemCols.map(c => <td key={c.field}>{item[c.field] ?? ''}</td>)}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="erp-pagination" data-testid="proc-pagination">
          <span>{fromRec} to {toRec} from {filtered.length} Records</span>
          <div className="erp-pagination-controls">
            <span>Show Rows</span>
            <select value={pageSize} onChange={e => { setPageSize(+e.target.value); setPage(1); }} data-testid="proc-page-size">
              {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span>Page</span>
            <input type="number" value={page} min={1} max={totalPages}
              onChange={e => setPage(Math.min(totalPages, Math.max(1, +e.target.value)))}
              data-testid="proc-page-input"/>
            <span>of {totalPages}</span>
            <button className="erp-pagination-btn" disabled={page === 1} onClick={() => setPage(1)}><ChevronsLeft size={11}/></button>
            <button className="erp-pagination-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={11}/></button>
            <button className="erp-pagination-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={11}/></button>
            <button className="erp-pagination-btn" disabled={page === totalPages} onClick={() => setPage(totalPages)}><ChevronsRight size={11}/></button>
          </div>
        </div>
      </div>
    </div>
  );
}
